import { DB_NAME, offlineDb } from "./db"
import type { SyncEngine } from "./sync-engine"

export const LAST_USER_ID_KEY = "stockline:lastUserId"
const LEGACY_IDB_NAME = "stockline-offline-db"
const REACT_QUERY_PERSIST_KEY = "REACT_QUERY_OFFLINE_CACHE"

let activeEngine: SyncEngine | null = null
const engineListeners = new Set<() => void>()

export function setActiveSyncEngine(engine: SyncEngine | null): void {
  activeEngine = engine
  // Notify subscribers of the registration change. A consumer that latched a
  // null engine (a cold deep-link mount racing the provider's registration
  // effect — child effects fire before parent effects) can then re-run once
  // the engine lands, instead of being stuck on a stale null read.
  for (const listener of engineListeners) listener()
}

export function getActiveSyncEngine(): SyncEngine | null {
  return activeEngine
}

/**
 * Subscribe to changes in the active SyncEngine registration — fires whenever
 * {@link setActiveSyncEngine} runs (engine registered at provider mount, or
 * cleared at logout/user-switch). Shaped for React's `useSyncExternalStore`
 * (paired with {@link getActiveSyncEngine} as the snapshot). Returns an
 * unsubscribe handle.
 */
export function subscribeActiveSyncEngine(listener: () => void): () => void {
  engineListeners.add(listener)
  return () => {
    engineListeners.delete(listener)
  }
}

export function getLastUserId(): string | null {
  if (typeof window === "undefined") return null
  return window.localStorage.getItem(LAST_USER_ID_KEY)
}

export function setLastUserId(id: string): void {
  if (typeof window === "undefined") return
  window.localStorage.setItem(LAST_USER_ID_KEY, id)
}

export function clearLastUserId(): void {
  if (typeof window === "undefined") return
  window.localStorage.removeItem(LAST_USER_ID_KEY)
}

/**
 * Cap on the best-effort change-queue drain at logout. A slow server or a
 * near-expired bearer must never make logout feel stuck — we'd rather drop
 * a few unsynced mutations than block the user on the way out.
 */
const FLUSH_TIMEOUT_MS = 3000

/**
 * Cap on `offlineDb.delete()`. IndexedDB blocks the delete request when any
 * other connection to the same DB stays open, and Dexie's promise then hangs
 * until that connection releases — with no rejection. We close THIS tab's own
 * connection first (see below), so the only remaining blocker is a genuine
 * second tab of the app. Without this cap, logout could wait forever on that
 * second tab; when the cap trips we let the caller continue and the queued
 * delete completes once the other tab releases.
 */
const DELETE_TIMEOUT_MS = 3000

export interface ResetOfflineStorageOptions {
  /**
   * If true and the browser is online, drain `_changeQueue` once before
   * wiping. Only meaningful for logout, where the bearer token is still
   * valid; user-switch and 401 paths pass false.
   */
  tryFlush: boolean
}

export async function resetOfflineStorage({
  tryFlush,
}: ResetOfflineStorageOptions): Promise<void> {
  const t0 = performance.now()
  const since = () => `${Math.round(performance.now() - t0)}ms`
  console.log(`[resetOfflineStorage] start (tryFlush=${tryFlush})`)

  if (
    tryFlush &&
    typeof navigator !== "undefined" &&
    navigator.onLine &&
    activeEngine
  ) {
    console.log(
      `[resetOfflineStorage] ${since()} flush queue (cap ${FLUSH_TIMEOUT_MS}ms)`
    )
    const flush = activeEngine.pushChanges().catch((err) => {
      console.warn("[resetOfflineStorage] flush failed:", err)
    })
    const timeout = new Promise<void>((resolve) =>
      setTimeout(resolve, FLUSH_TIMEOUT_MS)
    )
    await Promise.race([flush, timeout])
    console.log(`[resetOfflineStorage] ${since()} flush done (or capped)`)
  } else {
    console.log(`[resetOfflineStorage] ${since()} flush skipped`)
  }

  if (activeEngine) {
    console.log(`[resetOfflineStorage] ${since()} stopping engine`)
    // Guard against a synchronous throw from stop() — every other cleanup
    // step in this function is wrapped, and we must always null out the
    // reference so the rest of the chain (delete / clearLastUserId) runs
    // and a stale engine doesn't linger across logouts.
    try {
      activeEngine.stop()
    } catch (err) {
      console.error("[resetOfflineStorage] activeEngine.stop failed:", err)
    }
    activeEngine = null
  }

  // Close THIS tab's own Dexie connection before deleting. `engine.stop()`
  // only tears down sync/SSE — it leaves `offlineDb` open — and IndexedDB
  // blocks `deleteDatabase` while ANY connection is open, including our own.
  // That self-block is the root of a deadlock on the in-place reset paths
  // (user-switch / auth-error in `auth-provider.tsx`): no navigation follows
  // to release the connection, so the delete only ever reaches the blocked
  // state, and the re-mounted `AISyncProvider` then calls `offlineDb.open()`,
  // which queues behind the still-pending delete and never resolves — the
  // boot screen hangs forever. Closing first lets the single-tab delete
  // complete synchronously so the subsequent reopen/re-mount attaches cleanly.
  try {
    offlineDb.close()
  } catch (err) {
    console.error("[resetOfflineStorage] offlineDb.close failed:", err)
  }

  // Race the delete with a hard timeout — see DELETE_TIMEOUT_MS. Only reopen
  // if the delete actually completed; reopening a Dexie that's mid-deletion
  // reconnects to a DB the browser is about to drop, which would mask the
  // wipe and leak the previous user's data into the next session.
  console.log(
    `[resetOfflineStorage] ${since()} offlineDb.delete (cap ${DELETE_TIMEOUT_MS}ms)`
  )
  const deleteStatus = await Promise.race<"ok" | "timeout" | "error">([
    offlineDb
      .delete()
      .then(() => "ok" as const)
      .catch((err) => {
        console.error("[resetOfflineStorage] offlineDb.delete failed:", err)
        return "error" as const
      }),
    new Promise<"timeout">((resolve) =>
      setTimeout(() => resolve("timeout"), DELETE_TIMEOUT_MS)
    ),
  ])
  console.log(
    `[resetOfflineStorage] ${since()} offlineDb.delete → ${deleteStatus}`
  )

  if (deleteStatus === "timeout") {
    console.warn(
      "[resetOfflineStorage] offlineDb.delete blocked — another connection " +
        "(likely a second tab of this app) holds the database open. The " +
        "delete will complete once this tab navigates away; skipping reopen " +
        "to avoid attaching to a database the browser is about to drop."
    )
  } else if (deleteStatus === "ok") {
    try {
      await offlineDb.open()
      console.log(`[resetOfflineStorage] ${since()} offlineDb reopened`)
    } catch (err) {
      console.error("[resetOfflineStorage] offlineDb.open failed:", err)
    }
  }

  console.log(
    `[resetOfflineStorage] ${since()} deleting legacy IDB "${LEGACY_IDB_NAME}"`
  )
  await deleteIndexedDb(LEGACY_IDB_NAME)
  console.log(`[resetOfflineStorage] ${since()} legacy IDB done`)

  try {
    const { del } = await import("idb-keyval")
    await del(REACT_QUERY_PERSIST_KEY)
    console.log(`[resetOfflineStorage] ${since()} idb-keyval cleared`)
  } catch {
    // idb-keyval unavailable in some test envs.
  }

  clearLastUserId()
  console.log(`[resetOfflineStorage] ${since()} done`)
}

/**
 * Last-resort escape hatch for a wedged cold start (the boot-screen recovery
 * "Reset local data & reload" action). Closes this tab's Dexie connection and
 * best-effort deletes every offline IndexedDB database, then ALWAYS resolves so
 * the caller can hard-reload — a reload releases this tab's connection, and any
 * delete that was blocked by another tab completes once that tab releases.
 *
 * Deliberately narrower than {@link resetOfflineStorage}: it does NOT flush the
 * change queue (the app is stuck, the engine may never have started) and does
 * NOT touch `localStorage`, so the bearer token + last-user id survive and the
 * user stays logged in through the reload. It mirrors the documented manual
 * fix ("clear IndexedDB, keep localStorage, reload") that support currently
 * has to talk users through by hand.
 */
export async function hardResetOfflineStorage(): Promise<void> {
  try {
    activeEngine?.stop()
  } catch (err) {
    console.error("[hardResetOfflineStorage] engine.stop failed:", err)
  }
  activeEngine = null

  // Close our own connection first — IndexedDB blocks `deleteDatabase` while
  // any connection (including this tab's) is open.
  try {
    offlineDb.close()
  } catch (err) {
    console.error("[hardResetOfflineStorage] offlineDb.close failed:", err)
  }

  await Promise.all([
    deleteIndexedDb(DB_NAME),
    deleteIndexedDb(LEGACY_IDB_NAME),
  ])

  // React Query's persisted cache lives in idb-keyval; drop it too so a stale
  // snapshot can't re-wedge the next boot. Race it against the same hard
  // timeout as the DB deletes: on an unhealthy IndexedDB `del` can hang, and
  // this escape hatch MUST always settle so the caller can reload.
  try {
    const { del } = await import("idb-keyval")
    await Promise.race([
      del(REACT_QUERY_PERSIST_KEY),
      new Promise<void>((resolve) => setTimeout(resolve, DELETE_TIMEOUT_MS)),
    ])
  } catch {
    // idb-keyval unavailable in some environments — best-effort.
  }
}

function deleteIndexedDb(name: string): Promise<void> {
  if (typeof indexedDB === "undefined") return Promise.resolve()
  const remove = new Promise<void>((resolve) => {
    const req = indexedDB.deleteDatabase(name)
    req.onsuccess = () => resolve()
    req.onerror = () => {
      console.error(
        `[resetOfflineStorage] failed to delete IndexedDB "${name}"`,
        req.error
      )
      resolve()
    }
    // Another tab/connection is holding the DB open. The deletion will
    // queue and complete once that connection closes, but for the current
    // call the wipe did NOT happen — surface a warning so a stale-data
    // leak between users isn't silently swallowed. The hard reload that
    // follows will release this tab's connection and the next call to
    // resetOfflineStorage (e.g. on next logout) will succeed.
    req.onblocked = () => {
      console.warn(
        `[resetOfflineStorage] IndexedDB "${name}" deletion blocked — another connection holds it open. Stale data may persist until that connection releases.`
      )
      resolve()
    }
  })

  // The legacy `stockline-offline-db` is held open for the tab's lifetime by
  // the frozen hand-rolled cache (`frontend/src/lib/offline-db`), which never
  // closes its connection. With the DB open, `deleteDatabase` is blocked:
  // Chromium fires `onblocked` and we resolve, but Firefox fires NEITHER
  // `onblocked` NOR `onsuccess`, so the promise above hangs forever. This call
  // is awaited inside `resetOfflineStorage` on the in-place reset paths
  // (user-switch / auth-error), which gate the whole app behind their
  // completion — a hang there leaves Firefox users on a permanent white screen.
  // Cap the wait so the reset chain always completes; the queued deletion still
  // finishes once the blocking connection releases on the next reload.
  let timer: ReturnType<typeof setTimeout>
  const timeout = new Promise<void>((resolve) => {
    timer = setTimeout(() => {
      console.warn(
        `[resetOfflineStorage] IndexedDB "${name}" deletion timed out after ` +
          `${DELETE_TIMEOUT_MS}ms — a connection (legacy offline cache) holds ` +
          `it open. Continuing; the delete completes once that connection releases.`
      )
      resolve()
    }, DELETE_TIMEOUT_MS)
  })

  // Clear the timer once either settles so a delete that completes normally
  // can't fire a spurious "timed out" warning seconds later.
  return Promise.race([remove, timeout]).finally(() => clearTimeout(timer))
}
